import { WeighmentSitesAPI } from "api";
import axios from "axios";
import FalconLightBox from "components/common/FalconLightBox";
import Flex from "components/common/Flex";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Image, Row } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { FaImage } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { BASEURL, customSettings } from "utils/customConfig";
import { toast } from "react-toastify";
import {
  checkImageDimensions,
  getCitiesByState,
  getCountries,
  getStatesByCountry,
} from "utils/functions";
import ImageCropperModal from "components/image_crop/ImageCropperModal";
import StoreDocuments from "./StoreDocuments";

const BasicInformation = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const navigate = useNavigate();
  const { state } = useLocation();

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [mainImageFile, setMainImageFile] = useState(null);
  const maxCharactersLength = 255;

  const [showCropModal, setShowCropModal] = useState(false);
  const [cropType, setCropType] = useState("");
  const [tempImageFile, setTempImageFile] = useState(null);
  const [tempImagePreview, setTempImagePreview] = useState("");

  const LOGO_SIZE = window.imageConfig?.STORE_LOGO_SIZE || {
    width: 50,
    height: 50,
  };
  const MAIN_IMAGE_SIZE = window.imageConfig?.STORE_MAIN_IMAGE_SIZE || {
    width: 1920,
    height: 1080,
  };

  const handleCropResult = async (croppedFile) => {
    if (!croppedFile) return;
    setShowCropModal(false);

    const uploadedUrl = await uploadFile(croppedFile);

    if (cropType === "logo") {
      setValue("logo", uploadedUrl);
      setLogoFile(
        Object.assign(croppedFile, {
          preview: URL.createObjectURL(croppedFile),
        }),
      );
    }

    if (cropType === "main") {
      setValue("mainImage", uploadedUrl);
      setMainImageFile(
        Object.assign(croppedFile, {
          preview: URL.createObjectURL(croppedFile),
        }),
      );
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      const response = await axios.post("/Site/UploadFile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLoading(false);
      return response.data[0].destination;
    } catch (error) {
      toast.error("Failed to upload image");
      return "";
    }
  };

  // ---------- Dropzone for Logo ----------
  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } =
    useDropzone({
      multiple: false,
      accept: { "image/*": [".jpeg", ".png", ".jpg"] },
      onDrop: async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file) return;

        const dimensions = await checkImageDimensions(file);
        // if (dimensions.width > 50 || dimensions.height > 50) {
        //   toast.error("Maximum size must be 50x50px");
        //   return;
        // }

        setTempImageFile(file);
        setTempImagePreview(URL.createObjectURL(file));
        setCropType("logo");
        setShowCropModal(true);
      },
    });

  // ---------- Dropzone for Main Image ----------
  const {
    getRootProps: getMainImageRootProps,
    getInputProps: getMainImageInputProps,
  } = useDropzone({
    multiple: false,
    accept: { "image/*": [".jpeg", ".png", ".jpg"] },
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const dimensions = await checkImageDimensions(file);
      // if (dimensions.width > 1920 || dimensions.height > 1080) {
      //   toast.error("Maximum size must be 1920x1080px");
      //   return;
      // }

      setTempImageFile(file);
      setTempImagePreview(URL.createObjectURL(file));
      setCropType("main");
      setShowCropModal(true);
    },
  });

  console.log(state);
  console.log("mainImageFile", mainImageFile);

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  return (
    <>
      {loading ? (
        <div className="d-flex py-3 justify-content-center align-items-center">
          <Button variant="falcon-default" size="sm" className="fs-10">
            Loading...
          </Button>
        </div>
      ) : (
        <Card.Body
          className=""
          style={{
            height: `calc(100vh - 12.7rem)`,
            overflow: "auto",
            padding: "5px 20px",
          }}
        >
          <Row className="gx-2 ">
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  title="Name"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.businessName}
                  {...register("businessName")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.businessName?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="type"
                  type="text"
                  title="Type"
                  className="py-2"
                  isInvalid={!!errors?.type}
                  {...register("type")}
                >
                  <option value="">Select type</option>
                  <option value="Individual">Individual</option>
                  <option value="Organization">Organization</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.type?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="number"
                  placeholder="CNIC / Passport"
                  title="CNIC / Passport"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.cnic}
                  {...register("cnic")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.cnic?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="NTN"
                  title="NTN"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.ntn}
                  {...register("ntn")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.ntn?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Address"
                  title="Address"
                  maxLength={maxCharactersLength}
                  className="py-2 "
                  isInvalid={!!errors.address}
                  {...register("address")}
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.address?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="country"
                  type="text"
                  title="Country"
                  className="py-2"
                  isInvalid={!!errors?.country}
                  {...register("country")}
                >
                  <option value="">Select Country</option>
                  {getCountries()
                    ?.filter((d) => d.name === "Pakistan")
                    .map((d) => (
                      <option key={d.lat} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.country?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="state"
                  type="text"
                  title="State"
                  className="py-2 mb-1"
                  isInvalid={!!errors?.state}
                  {...register("state")}
                >
                  <option value="">Select State</option>
                  {getStatesByCountry(selectedCountry)?.map((d) => (
                    <option key={d.lat} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.state?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group>
                <Form.Select
                  aria-label="city"
                  type="text"
                  title="City"
                  className="py-2 mb-1"
                  isInvalid={!!errors?.city}
                  {...register("city")}
                >
                  <option value="">Select City</option>
                  {getCitiesByState(selectedCountry, selectedState)?.map(
                    (d) => (
                      <option key={d.lat} value={d.name}>
                        {d.name}
                      </option>
                    ),
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors?.city?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md="6">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Postal Code"
                  title="Postal Code"
                  maxLength={maxCharactersLength}
                  className="py-2"
                  isInvalid={!!errors.postalCode}
                  {...register("postalCode")}  
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-inline w-75"
                  >
                    {errors.postalCode?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md="6">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div className="d-flex gap-3">
                  <div className="d-flex">
                    <div {...getLogoRootProps()}>
                      <input {...getLogoInputProps({ multiple: false })} />
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        // className="mt-2"
                      >
                        <FaImage
                          size={27}
                          title="Logo"
                          className="cursor-pointer"
                        />

                        <p
                          className="fs-9 mb-0 px-2 cursor-pointer"
                          style={{ margin: 0, padding: 0, lineHeight: "1" }}
                        >
                          <span
                            style={{ margin: 0, padding: 0, lineHeight: "1" }}
                          >
                            Logo
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "10px",
                              color: "red",
                              margin: 0,
                              padding: 0,
                              lineHeight: "1",
                            }}
                          >
                            ({LOGO_SIZE?.width}px{" "}
                            <span style={{ color: "orange" }}>x</span>{" "}
                            {LOGO_SIZE.height}px)
                          </span>
                        </p>
                      </Flex>
                    </div>

                    {/* <Col md={file || (state?.logoFile?.fileBase64 ? "2" : "1")}> */}
                    {logoFile || state?.logo ? (
                      <FalconLightBox
                        image={
                          logoFile
                            ? logoFile.preview
                            : `${BASEURL}/wwwroot${state.logo}`
                        }
                      >
                        <Image
                          src={
                            logoFile
                              ? logoFile.preview
                              : `${BASEURL}/wwwroot${state.logo}`
                          }
                          rounded
                          width={80}
                          height={40}
                        />
                      </FalconLightBox>
                    ) : null}
                    {/* Render nothing if both file and state.logoFile.fileBase64 are not present */}
                  </div>
                  <div className="d-flex">
                    <div {...getMainImageRootProps()}>
                      <input {...getMainImageInputProps({ multiple: false })} />
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        // className="mt-2"
                      >
                        <FaImage
                          size={27}
                          title="Main Image"
                          className="cursor-pointer"
                        />
                        <p
                          className="fs-9 mb-0 px-2 cursor-pointer"
                          style={{ margin: 0, padding: 0, lineHeight: "1" }}
                        >
                          <span
                            style={{ margin: 0, padding: 0, lineHeight: "1" }}
                          >
                            Main Image
                          </span>
                          <br />
                          <span
                            style={{
                              fontSize: "10px",
                              color: "red",
                              margin: 0,
                              padding: 0,
                              lineHeight: "1",
                            }}
                          >
                            ({MAIN_IMAGE_SIZE.width}px{" "}
                            <span style={{ color: "orange" }}>x</span>{" "}
                            {MAIN_IMAGE_SIZE.height}px)
                          </span>
                        </p>
                      </Flex>
                    </div>

                    {/* <Col md={file || (state?.logoFile?.fileBase64 ? "2" : "1")}> */}
                    {mainImageFile || state?.mainImage ? (
                      <FalconLightBox
                        image={
                          mainImageFile
                            ? mainImageFile.preview
                            : `${BASEURL}/wwwroot${state.mainImage}`
                        }
                      >
                        <Image
                          src={
                            mainImageFile
                              ? mainImageFile.preview
                              : `${BASEURL}/wwwroot${state.mainImage}`
                          }
                          rounded
                          width={80}
                          height={40}
                        />
                      </FalconLightBox>
                    ) : null}
                    {/* Render nothing if both file and state.logoFile.fileBase64 are not present */}
                  </div>
                </div>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="defaultSwitch"
                    label="Active"
                    className="mt-2"
                    defaultChecked={!state}
                    isInvalid={!!errors.status}
                    {...register("status")}
                  />
                </Form.Group>
              </div>
            </Col>
            {/* <Col md="6">
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="defaultSwitch"
                  label="Active"
                  className="mt-2"
                  defaultChecked={!state}
                  isInvalid={!!errors.status}
                  {...register("status")}
                />

              </Form.Group>
            </Col> */}
          </Row>

          <h5 className="fs-10 mb-2 py-xl-0 text-muted">Store Documents</h5>

          <StoreDocuments />
        </Card.Body>
      )}

      <ImageCropperModal
        show={showCropModal}
        imageUrl={tempImagePreview}
        type={cropType}
        onClose={() => setShowCropModal(false)}
        onCropped={handleCropResult}
      />
    </>
  );
};

export default BasicInformation;
